/**
 * Visibility catalog — the SINGLE SOURCE OF TRUTH for the data-visibility axis
 * ("Informationen", Story V10-E) — Stefan's model from the 2026-07-01 call.
 *
 * WHAT THIS AXIS IS (decoupled from the Epic-12 operation permissions):
 *   - Binary per user ("schwarz/weiss"); granularity is NOT per field but per
 *     Kachel → Tab → Menüpunkt (extreme case: a whole Menüpunkt hidden).
 *   - Modelled as the NEGATIVFALL (deny-list): default = everything visible;
 *     a role explicitly SUBTRACTS keys via role_visibility_deny rows.
 *   - Membership in several groups is KUMULATIV → a key is hidden only if EVERY
 *     role the user holds denies it (the union of what the roles let you see;
 *     effective denied set = INTERSECTION of the per-role deny sets).
 *   - Canonical sensitive examples: Courtage/revenue info + Risikostufe.
 *
 * DESIGN DECISIONS baked in (exactly the Epic-12 permission-catalog pattern):
 *
 * 1. CATALOG = CODE, NOT A TABLE. The DB stores only the per-role DENY selections
 *    (role_visibility_deny rows, migration 0054). The catalog itself is never
 *    persisted; keys can be added in future stories with no migration.
 *
 * 2. HIERARCHY BY DOT-PATH: denying a key hides it AND all descendants
 *    (segment-boundary prefix — denied `kunden` hides `kunden.finanzen`, NOT a
 *    hypothetical `kundenx`). See isVisibilityDenied().
 *
 * 3. `as const satisfies readonly VisibilityKeyDefinition[]` keeps each `key` as
 *    a narrow string-literal type (for the VisibilityKey union) while TypeScript
 *    still checks the shape — same derivation pattern as PermissionKey/PERMISSION_KEYS.
 *
 * 4. Keys are lowercase ASCII dot-path segments (`praemie`, `aktivitaet`);
 *    labels carry the real German domain terms (coding-standards §1).
 *
 * [Source: docs/concept/2026-07-01_meeting-funktionen-berechtigungsgruppen.md §1 + §3]
 * [Source: docs/stories/v10-e-visibility-layer.md AC1 + Dev Notes "The catalog seed"]
 * [Source: packages/shared/src/permission.ts (catalog-as-code precedent)]
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * The three hierarchy levels of the visibility axis (coarse → fine):
 * 'menuepunkt' — a whole sidebar area (extreme case: entire Menüpunkt hidden)
 * 'tab'        — a tab inside an entity-detail view (Kunde/Police)
 * 'kachel'     — a single card/KPI block inside a view
 */
export type VisibilityLevel = 'menuepunkt' | 'tab' | 'kachel';

/**
 * Shape of a single visibility-key definition in the catalog.
 *
 * `key`         — dot-path ASCII identifier, globally unique; the dot-path encodes
 *                 the hide-hierarchy (denying a key hides all descendants).
 * `level`       — one of the three VisibilityLevel values.
 * `parent`      — the parent catalog key (null for top-level Menüpunkte). A parent
 *                 must itself be a catalog key. NOTE: `parent` documents the UI
 *                 nesting; the DENY hierarchy is the dot-path (they coincide).
 * `label`       — user-visible label for the config UI (German domain terms).
 * `description` — one-line English helper text (what the area shows).
 * `sensitive`   — true for the flagship sensitive areas (Courtage / Risikostufe);
 *                 purely informational for the config UI (highlighting), carries
 *                 NO enforcement semantics.
 * `wire_ready`  - POLARITY: true = wire-ready but NOT yet backed by data, so the config
 *                 API stops OFFERING it as a new deny (visibility-deny.service.ts). The
 *                 key STAYS in the catalog (VISIBILITY_KEY_LIST validation, resolver,
 *                 stored denies keep working). Drop the flag when the backing data lands.
 */
export interface VisibilityKeyDefinition {
  key: string;
  level: VisibilityLevel;
  parent: string | null;
  label: string;
  description: string;
  sensitive?: boolean;
  wire_ready?: boolean;
}

// ── Visibility catalog (the seed — Story V10-E AC1) ──────────────────────────

/**
 * The static visibility catalog covering every existing app area.
 *
 * Menüpunkte come from the shipped web sidebar (Arbeit: Heute/Kommunikation/
 * Aufgaben/Renewals/Rechnungen · Daten: Kunden/Policen/Versicherer · plus Brok
 * and the Courtage area), tabs from the shipped T2 entity-detail shell, Kacheln
 * from T2 + V10 Kap. 16 + the Bereich 3 section/card layer (one Kachel key per
 * real card on each Kunde/Police client-detail tab — Berechtigungskonzept V3.0
 * Bereich 3, Stefan's tab-AND-card visibility clarification). The card layer is
 * a level finer than the tab and coarser than the per-field field_permissions
 * (WS1); a card deny REMOVES the card from the client-detail UI for that role.
 *
 * Flagship sensitive keys (⭐ sensitive: true):
 *   courtage · kunden.finanzen · kunden.risiko · policen.praemie · policen.risiko
 *
 * `kunden.risiko` and `policen.risiko` additionally carry `wire_ready: true`: no risk
 * data exists yet (KundeDto has no risiko field; PolicyListItemDto.risikostufe is always
 * null), so the config API no longer offers them as new denies. They stay in the catalog
 * and their enforcement points stay wired; drop the flag when the risk module lands.
 *
 * [Source: docs/stories/v10-e-visibility-layer.md Dev Notes "THE CATALOG SEED"]
 */
export const VISIBILITY_CATALOG = [

  // ── Menüpunkte (sidebar areas) ─────────────────────────────────────────────
  {
    key: 'heute',
    level: 'menuepunkt',
    parent: null,
    label: 'Heute',
    description: 'The daily dashboard entry point (tasks, signals, KPIs for today).',
  },
  {
    key: 'kommunikation',
    level: 'menuepunkt',
    parent: null,
    label: 'Kommunikation',
    description: 'The communication inbox (emails, letters, channel deliveries).',
  },
  {
    key: 'aufgaben',
    level: 'menuepunkt',
    parent: null,
    label: 'Aufgaben',
    description: 'The Pendenzen work list (open tasks and their split view).',
  },
  {
    key: 'renewals',
    level: 'menuepunkt',
    parent: null,
    label: 'Renewals',
    description: 'The renewal alert board (expiring policies and Kündigungsfristen).',
  },
  {
    key: 'rechnungen',
    level: 'menuepunkt',
    parent: null,
    label: 'Rechnungen',
    description: 'The Rechnungskontrolle area (incoming invoices and check results).',
  },
  {
    key: 'kunden',
    level: 'menuepunkt',
    parent: null,
    label: 'Kunden',
    description: 'The customer area (persons and firmas with their detail views).',
  },
  {
    key: 'policen',
    level: 'menuepunkt',
    parent: null,
    label: 'Policen',
    description: 'The policy area (policy list and policy detail views).',
  },
  {
    key: 'versicherer',
    level: 'menuepunkt',
    parent: null,
    label: 'Versicherer',
    description: 'The carrier area (insurer master data and register info).',
  },
  {
    key: 'brok',
    level: 'menuepunkt',
    parent: null,
    label: 'Brok',
    description: 'The Brok in-app AI assistant (chat panel).',
  },
  // ⭐ flagship — the whole commission area (backend live, nav item pending)
  {
    key: 'courtage',
    level: 'menuepunkt',
    parent: null,
    label: 'Courtageabrechnung',
    description: 'The commission area (statements, tariffs, splits, Akonto, Kennzahlen).',
    sensitive: true,
  },

  // ── Kunde detail tabs (T2 entity-detail shell) ─────────────────────────────
  {
    key: 'kunden.uebersicht',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Übersicht',
    description: 'The customer overview tab (Stammdaten, KPIs, status).',
  },
  {
    key: 'kunden.policen',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Policen',
    description: 'The customer policies tab (policies held by this customer).',
  },
  {
    key: 'kunden.rechnungen',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Rechnungen',
    description: 'The customer invoices tab (invoices and check results).',
  },
  {
    key: 'kunden.kommunikation',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Kommunikation',
    description: 'The customer communication tab (correspondence timeline).',
  },
  {
    key: 'kunden.beziehungen',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Beziehungen',
    description: 'The customer relationships tab (person-firma links).',
  },
  {
    key: 'kunden.dokumente',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Dokumente',
    description: 'The customer documents tab (uploaded and generated documents).',
  },
  {
    key: 'kunden.aktivitaet',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Aktivität',
    description: 'The customer activity tab (audit-backed activity timeline).',
  },
  // P2 Placement Slice 1 — the Kunde «Risikoprofil» tab (Ausschreibung Ch 7.1).
  {
    key: 'kunden.risikoprofil',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Risikoprofil',
    description: 'The customer risk-profile tab (generelles Risikoprofil + Basissparten-Checkliste).',
  },
  // P2 Placement Slice 4 — the Kunde «Ausschreibungen» tab (Placements; Ausschreibung Ch 7.3).
  {
    key: 'kunden.ausschreibungen',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Ausschreibungen',
    description: 'The customer Ausschreibungen tab (Placements list, pipeline, and create).',
  },
  // The Kunde «Mandate & Eintragungen» tab (Brokermandat Kap. 7.7): one row per insurer, both mandates.
  {
    key: 'kunden.mandate_eintragungen',
    level: 'tab',
    parent: 'kunden',
    label: 'Kunde: Mandate & Eintragungen',
    description: 'The customer tab with the registrations at the insurers (Analysemandat and Brokermandat side by side, Nachweis-Dossier).',
  },

  // ── Kunde Kacheln ──────────────────────────────────────────────────────────
  {
    key: 'kunden.uebersicht.jahrespraemie',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Jahresprämie (KPI)',
    description: 'The annual-premium KPI card on the customer overview tab.',
  },
  // ⭐ V10 Kap. 16.1 — IBAN (sensibel) / Honorar-/Courtagevereinbarung
  {
    key: 'kunden.finanzen',
    level: 'kachel',
    parent: 'kunden',
    label: 'Kachel: Finanzen & Abrechnung',
    description: 'The customer finance card (IBAN, Honorar-/Courtagevereinbarung).',
    sensitive: true,
  },
  // ⭐ flagship risk — Kopfbereich Risiko-Badge
  // wire_ready: KundeDto carries no risiko field, so the badge never renders from live data.
  {
    key: 'kunden.risiko',
    level: 'kachel',
    parent: 'kunden',
    label: 'Kachel: Risikostufe',
    description: 'The customer risk-level badge in the detail header area.',
    sensitive: true,
    wire_ready: true,
  },

  // ── Kunde Tab-Karten (Bereich 3 — section/card-level visibility) ────────────
  // One Kachel key per real card/section on the Kunde client-detail tabs
  // (enumerated from apps/web/src/features/entity-detail/KundeDetailScreen.tsx).
  // A deny removes the card from the client-detail UI for the role; denying the
  // parent tab (or Menüpunkt) still cascades and hides all of these too.
  {
    key: 'kunden.uebersicht.hinweise',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: KI-Hinweise',
    description: 'The KI recommendation / cross-selling insight cards on the customer overview tab.',
  },
  {
    key: 'kunden.uebersicht.kennzahlen',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Kennzahlen',
    description: 'The KPI row (Policen, Jahresprämie, offene Rechnungen, nächstes Renewal) on the overview tab.',
  },
  {
    key: 'kunden.uebersicht.vorgang',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Vorgang & Mandatsstatus',
    description: 'The mandate Vorgang lifecycle card on the customer overview tab.',
  },
  {
    key: 'kunden.uebersicht.domains',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: E-Mail-Domains',
    description: 'The company e-mail-domains card on the customer overview tab (firma only).',
  },
  {
    key: 'kunden.uebersicht.stammdaten',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Firmenstammdaten',
    description: 'The company master-data card on the customer overview tab (firma only).',
  },
  {
    key: 'kunden.uebersicht.kontakte',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Kontakte',
    description: 'The contacts card (Hauptansprechperson) on the customer overview tab (firma only).',
  },
  {
    key: 'kunden.uebersicht.zeichnungsberechtigte',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Zeichnungsberechtigte',
    description: 'The register signatories card (who may sign for the firm) on the customer overview tab (firma only).',
  },
  {
    key: 'kunden.uebersicht.meldung',
    level: 'kachel',
    parent: 'kunden.uebersicht',
    label: 'Kachel: Versicherermeldung',
    description: 'The insurer-notification workflow card on the customer overview tab.',
  },
  {
    key: 'kunden.policen.liste',
    level: 'kachel',
    parent: 'kunden.policen',
    label: 'Kachel: Policenliste',
    description: 'The policies list card on the customer policies tab.',
  },
  {
    key: 'kunden.rechnungen.liste',
    level: 'kachel',
    parent: 'kunden.rechnungen',
    label: 'Kachel: Rechnungsliste',
    description: 'The invoices list card on the customer invoices tab.',
  },
  {
    key: 'kunden.kommunikation.verlauf',
    level: 'kachel',
    parent: 'kunden.kommunikation',
    label: 'Kachel: Kommunikationsverlauf',
    description: 'The correspondence feed card on the customer communication tab.',
  },
  {
    key: 'kunden.beziehungen.netz',
    level: 'kachel',
    parent: 'kunden.beziehungen',
    label: 'Kachel: Beziehungsnetz',
    description: 'The relationship-network card on the customer relationships (Struktur) tab.',
  },
  {
    key: 'kunden.dokumente.liste',
    level: 'kachel',
    parent: 'kunden.dokumente',
    label: 'Kachel: Dokumentenliste',
    description: 'The documents list card on the customer documents tab.',
  },
  {
    key: 'kunden.aktivitaet.verlauf',
    level: 'kachel',
    parent: 'kunden.aktivitaet',
    label: 'Kachel: Aktivitätsverlauf',
    description: 'The activity-timeline card on the customer activity tab.',
  },

  // ── Kunde Risikoprofil-Karten (P2 Placement Slice 1 — the 3 sections of the tab) ─
  // Section-level Kacheln on the Risikoprofil tab; a deny removes that section for the role.
  // Not flagship-sensitive (the gated Gesundheit/Einkommen fields carry their own field-right).
  {
    key: 'kunden.risikoprofil.generell',
    level: 'kachel',
    parent: 'kunden.risikoprofil',
    label: 'Kachel: Generelles Risikoprofil',
    description: 'The generelles-Risikoprofil section (atomic fields per category) on the customer risk-profile tab.',
  },
  {
    key: 'kunden.risikoprofil.basissparten',
    level: 'kachel',
    parent: 'kunden.risikoprofil',
    label: 'Kachel: Basissparten-Checkliste',
    description: 'The Basissparten-Checkliste section (Soll + Ampel) on the customer risk-profile tab.',
  },
  {
    key: 'kunden.risikoprofil.details',
    level: 'kachel',
    parent: 'kunden.risikoprofil',
    label: 'Kachel: Details je Police',
    description: 'The per-policy risk/coverage links section on the customer risk-profile tab.',
  },

  // ── Kunde Ausschreibungen-Karte (P2 Placement Slice 4 — the list section of the tab) ─
  // The KPIs/filter/list card on the Ausschreibungen tab; a deny removes it for the role.
  {
    key: 'kunden.ausschreibungen.liste',
    level: 'kachel',
    parent: 'kunden.ausschreibungen',
    label: 'Kachel: Ausschreibungsliste',
    description: 'The Placements list/pipeline card (KPIs + filter + Liste/Pipeline) on the customer Ausschreibungen tab.',
  },

  // ── Police detail tabs (T2 entity-detail shell) ────────────────────────────
  {
    key: 'policen.uebersicht',
    level: 'tab',
    parent: 'policen',
    label: 'Police: Übersicht',
    description: 'The policy overview tab (core policy data and current version).',
  },
  {
    key: 'policen.rechnungen',
    level: 'tab',
    parent: 'policen',
    label: 'Police: Rechnungen',
    description: 'The policy invoices tab (invoices booked against this policy).',
  },
  {
    key: 'policen.dokumente',
    level: 'tab',
    parent: 'policen',
    label: 'Police: Dokumente',
    description: 'The policy documents tab (policy documents and endorsements).',
  },
  {
    key: 'policen.aktivitaet',
    level: 'tab',
    parent: 'policen',
    label: 'Police: Aktivität',
    description: 'The policy activity tab (audit-backed activity timeline).',
  },
  // P2 Placement Slice 2 — the Police «Risiko & Deckung» tab (Ausschreibung Ch 7.2).
  {
    key: 'policen.risiko_deckung',
    level: 'tab',
    parent: 'policen',
    label: 'Police: Risiko & Deckung',
    description: 'The policy risk-and-coverage tab (branchenspezifische Ist-Attribute, Soll-Deckung & Gap).',
  },

  // ── Police Kacheln ─────────────────────────────────────────────────────────
  // ⭐ V10 Kap. 16.2 — premium/Stempelsteuer/fees + Courtage Satz/Betrag
  {
    key: 'policen.praemie',
    level: 'kachel',
    parent: 'policen',
    label: 'Kachel: Prämie & Abrechnung',
    description: 'The policy premium card (premium, Stempelsteuer, fees, Courtage rate/amount).',
    sensitive: true,
  },
  // ⭐ flagship risk — V10 15.3 (catalog-only; module not built)
  {
    key: 'policen.risiko',
    level: 'kachel',
    parent: 'policen',
    label: 'Kachel: AVB-Risikoeinschätzung',
    description: 'The policy AVB risk-assessment card (risk level and rationale).',
    sensitive: true,
    wire_ready: true,
  },

  // ── Police Tab-Karten (Bereich 3 — section/card-level visibility) ───────────
  // One Kachel key per real card/section on the Police detail tabs
  // (enumerated from apps/web/src/features/entity-detail/PoliceDetailScreen.tsx).
  {
    key: 'policen.uebersicht.stammdaten',
    level: 'kachel',
    parent: 'policen.uebersicht',
    label: 'Kachel: Police-Stammdaten',
    description: 'The policy master-data fact card on the policy overview (Stammdaten) tab.',
  },
  {
    key: 'policen.uebersicht.finanzen',
    level: 'kachel',
    parent: 'policen.uebersicht',
    label: 'Kachel: Finanzen',
    description: 'The finance fact card (Jahresprämie, Nettoprämie, Stempelabgabe) on the policy overview tab.',
  },
  {
    key: 'policen.uebersicht.dokument',
    level: 'kachel',
    parent: 'policen.uebersicht',
    label: 'Kachel: Dokumente öffnen',
    description: 'The source-document opener card on the policy overview tab.',
  },
  {
    key: 'policen.rechnungen.liste',
    level: 'kachel',
    parent: 'policen.rechnungen',
    label: 'Kachel: Rechnungsliste',
    description: 'The invoices list card on the policy invoices tab.',
  },
  {
    key: 'policen.dokumente.avb',
    level: 'kachel',
    parent: 'policen.dokumente',
    label: 'Kachel: Versicherungsbedingungen (AVB)',
    description: 'The AVB / policy-conditions card on the policy documents tab.',
  },
  {
    key: 'policen.aktivitaet.verlauf',
    level: 'kachel',
    parent: 'policen.aktivitaet',
    label: 'Kachel: Aktivitätsverlauf',
    description: 'The activity-timeline card on the policy activity tab.',
  },

  // ── Police Risiko-&-Deckung-Karten (P2 Placement Slice 2 — the sections of the tab) ─
  // Section-level Kacheln on the «Risiko & Deckung» tab; a deny removes that section for the
  // role. `.schadenverlauf` is reserved for Slice 6 (the real Schadenverlauf section).
  {
    key: 'policen.risiko_deckung.ist',
    level: 'kachel',
    parent: 'policen.risiko_deckung',
    label: 'Kachel: Ist-Attribute',
    description: 'The branchenspezifische Ist-Attribute section on the policy risk-and-coverage tab.',
  },
  {
    key: 'policen.risiko_deckung.soll',
    level: 'kachel',
    parent: 'policen.risiko_deckung',
    label: 'Kachel: Soll-Deckung & Gap',
    description: 'The Soll-Deckung and Gap-Analyse section on the policy risk-and-coverage tab.',
  },
  {
    key: 'policen.risiko_deckung.objekte',
    level: 'kachel',
    parent: 'policen.risiko_deckung',
    label: 'Kachel: Versicherte Objekte',
    description: 'The insured-objects list (per-object Ist-Attribute) on the policy risk-and-coverage tab.',
  },
  {
    key: 'policen.risiko_deckung.schadenverlauf',
    level: 'kachel',
    parent: 'policen.risiko_deckung',
    label: 'Kachel: Schadenverlauf',
    description: 'The Schadenverlauf section on the policy risk-and-coverage tab (reserved for Slice 6).',
  },

] as const satisfies readonly VisibilityKeyDefinition[];

// ── Derived types + helpers ───────────────────────────────────────────────────

/**
 * Union type of every valid visibility key (narrow string literals).
 * Import this for type-safe references in the deny-config API and the web shell.
 */
export type VisibilityKey = (typeof VISIBILITY_CATALOG)[number]['key'];

/**
 * Flat readonly array of every visibility key (in catalog order).
 * Used by the admin deny-config API (key validation) and the config UI.
 */
export const VISIBILITY_KEY_LIST = VISIBILITY_CATALOG.map((v) => v.key) as readonly VisibilityKey[];

// ── Flagship named constants (no magic strings at call sites) ────────────────

/** The whole commission area — the flagship sensitive Menüpunkt (server-side gate 7a). */
export const VISIBILITY_KEY_COURTAGE: VisibilityKey = 'courtage';

/** The policy premium Kachel — flagship sensitive block (server-side gate 7b). */
export const VISIBILITY_KEY_POLICE_PRAEMIE: VisibilityKey = 'policen.praemie';

/** The customer finance Kachel (IBAN / Honorar-/Courtagevereinbarung — V10 16.1). */
export const VISIBILITY_KEY_KUNDE_FINANZEN: VisibilityKey = 'kunden.finanzen';

/** The customer risk-level Kachel (Risiko-Badge — flagship risk key). */
export const VISIBILITY_KEY_KUNDE_RISIKO: VisibilityKey = 'kunden.risiko';

/** The policy AVB risk-assessment Kachel (V10 15.3 — catalog-only for now). */
export const VISIBILITY_KEY_POLICE_RISIKO: VisibilityKey = 'policen.risiko';

// ── Customer-detail sub-resource TAB keys (Kap 5.5 server-side deny enforcement) ──
// These name the Kunde-detail tabs whose RAW sub-resource read endpoints are gated
// server-side (not just UI-hidden): a role that DENIES the tab but HOLDS the function
// key must not pull the data via the raw API ("unsichtbar auch in Suche, Export,
// Schnittstelle"). Each is enforced on its subject/client-scoped read path.

/** The customer Policen tab — gates GET /policies?subject_id= (customer-scoped). */
export const VISIBILITY_KEY_KUNDEN_POLICEN: VisibilityKey = 'kunden.policen';

/** The customer Kommunikation tab — gates GET /communications (per-customer scope). */
export const VISIBILITY_KEY_KUNDEN_KOMMUNIKATION: VisibilityKey = 'kunden.kommunikation';

/** The customer Dokumente tab — gates GET /documents?client_id= (client-scoped). */
export const VISIBILITY_KEY_KUNDEN_DOKUMENTE: VisibilityKey = 'kunden.dokumente';

/**
 * The customer Risikoprofil tab (P2 Placement Slice 1) — gates every risk-profile and
 * coverage-baseline read/write endpoint server-side (Kap 5.5: a role that denies the tab must
 * not pull the data via the raw API; the api returns 404 not_found on deny).
 */
export const VISIBILITY_KEY_KUNDEN_RISIKOPROFIL: VisibilityKey = 'kunden.risikoprofil';

/**
 * The customer Ausschreibungen tab (P2 Placement Slice 4) — gates every /placements* read/write
 * endpoint server-side (Kap 5.5: a role that denies the tab must not pull the data via the raw
 * API; the api returns 404 not_found on deny).
 */
export const VISIBILITY_KEY_KUNDEN_AUSSCHREIBUNGEN: VisibilityKey = 'kunden.ausschreibungen';

/**
 * The Kunde «Mandate & Eintragungen» tab (the registrations at the insurers): gates the area, its tile, the
 * Nachweis-Dossier and the mirror on the Versicherer, in the web and on the API (404 on deny).
 */
export const VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN: VisibilityKey = 'kunden.mandate_eintragungen';

/**
 * The Police «Risiko & Deckung» tab (P2 Placement Slice 2) — gates every risk-attributes and
 * target-coverage read/write endpoint server-side (Kap 5.5: a role that denies the tab must not
 * pull the data via the raw API; the api returns 404 not_found on deny).
 */
export const VISIBILITY_KEY_POLICEN_RISIKO_DECKUNG: VisibilityKey = 'policen.risiko_deckung';

/**
 * The Police «Schadenverlauf» Kachel (P2 Placement Slice 6) — gates the claims-history read/retrieve/
 * rohdaten endpoints and the Tab-1 read-only reference server-side (Kap 5.5). A denied PARENT
 * (`policen.risiko_deckung`) also denies this Kachel via isVisibilityKeyDenied's ancestor resolution.
 * The catalog entry already exists (reserved in Slice 2) — this is a named constant only, no new entry.
 */
export const VISIBILITY_KEY_POLICEN_RISIKO_DECKUNG_SCHADENVERLAUF: VisibilityKey =
  'policen.risiko_deckung.schadenverlauf';

/**
 * Is `key` hidden given an effective denied-key set?
 *
 * Exact-or-ancestor matching on dot-path segment boundaries:
 *   - `denied.has(key)` → denied.
 *   - any dot-path ancestor prefix of `key` in `denied` → denied
 *     (denied `kunden` hides `kunden.finanzen` and `kunden.uebersicht.jahrespraemie`).
 *   - Segment boundary is respected: denied `kunden` does NOT hide a key that
 *     merely starts with the same letters (`kundenx`).
 *   - `undefined` / empty set → nothing denied (the fail-open contract: visibility
 *     is a subtractive shaping layer; the Epic-12 permission gates stay the hard
 *     authorization line).
 *
 * Pure function, no side effects.
 *
 * @param denied - the caller's effective denied-key set (resolver output), or undefined
 * @param key    - the visibility key to test (typically a VisibilityKey constant)
 * @returns true iff the key (or one of its dot-path ancestors) is in the denied set
 */
export function isVisibilityDenied(
  denied: ReadonlySet<string> | undefined,
  key: string,
): boolean {
  if (denied === undefined || denied.size === 0) return false;
  if (denied.has(key)) return true;

  // Walk the dot-path ancestors: 'a.b.c' → check 'a', then 'a.b'.
  const segments = key.split('.');
  let prefix = '';
  for (let i = 0; i < segments.length - 1; i++) {
    prefix = i === 0 ? segments[0]! : `${prefix}.${segments[i]!}`;
    if (denied.has(prefix)) return true;
  }
  return false;
}

/**
 * Intersect several roles' visibility-deny sets under the KUMULATIV
 * (union-of-visibility) contract, ANCESTOR-AWARE (Story V10-E; fixes B1).
 *
 * A user in several groups may see a key if ANY role lets them see it, so a key
 * is effectively hidden ONLY when EVERY held role denies it. The catch: a role
 * denies a key when it denies that key OR any dot-path ancestor of it
 * (isVisibilityDenied). A raw string-set intersection MISSES the ancestor link:
 * role A denies `policen` and role B denies `policen.praemie` both hide
 * `policen.praemie`, yet {policen} ∩ {policen.praemie} = ∅ (raw) would UNLOCK it.
 *
 * This helper predicate-intersects instead: the candidate keys are the union of
 * every role's raw deny keys, and a candidate survives only if isVisibilityDenied
 * is true for EVERY role's set. The surviving keys are the deepest keys all roles
 * agree on; downstream isVisibilityDenied() then cascades them to descendants.
 * A role with an EMPTY deny set denies nothing → the result collapses to ∅
 * (one unrestricted role restores full visibility), matching the raw semantics.
 *
 * Pure function, no side effects. Kept HERE (not in the resolver) so the deny
 * semantics live in ONE place next to isVisibilityDenied (coding-standards rule #2).
 *
 * @param perRoleSets - one deny-key set per held role (order irrelevant)
 * @returns the effective denied-key set (a key is present iff every role denies it)
 */
export function intersectVisibilityDenySets(
  perRoleSets: ReadonlyArray<ReadonlySet<string>>,
): Set<string> {
  if (perRoleSets.length === 0) return new Set<string>();
  // A single deny-free role already unlocks everything — short-circuit to ∅.
  if (perRoleSets.some((s) => s.size === 0)) return new Set<string>();

  // Candidate keys = the union of every role's raw deny keys.
  const candidates = new Set<string>();
  for (const s of perRoleSets) {
    for (const k of s) candidates.add(k);
  }

  const effective = new Set<string>();
  for (const key of candidates) {
    // Effectively denied iff EVERY role denies it (exact OR via a dot-path ancestor).
    if (perRoleSets.every((s) => isVisibilityDenied(s, key))) effective.add(key);
  }
  return effective;
}

// ── Wire types ────────────────────────────────────────────────────────────────

/**
 * Response body for GET /me/effective-visibility (Story V10-E AC4).
 * The web uses this to hide Kacheln/Tabs/Menüpunkte; it never recomputes the
 * deny semantics client-side (coding-standards rule #2) — it calls
 * isVisibilityDenied() with this set and renders the result.
 */
export interface EffectiveVisibilityResponse {
  /** The caller's effective denied visibility keys, sorted ascending. */
  denied_keys: string[];
}

/**
 * Response body for GET /admin/roles/:roleId/visibility-denies (Story V10-E AC5).
 */
export interface RoleVisibilityDeniesResponse {
  /** The role's denied visibility keys, sorted ascending. */
  denied_keys: string[];
}
